import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Container from '../../../Common/Container';
import Grid from '../../../Common/Grid';
import Button from '../../../Common/Button';
import Header from '../../../../containers/User/HeaderContainer';
import { AuthContext } from '../../../../common/authContext';
import theme from '../../../../common/theme';
import BackToAdminPanel from '../BackToAdminPanel/BackToAdminPanel';
import messages from './messages';
import { Partner, PartnerType } from '../../../../models/partner.model';
import { Tenant } from '../../../../models/tenant.model';
import {
  createPartner,
  deletePartner,
  getPartners,
  updatePartner,
  updatePartnerRelations
} from '../../../../services/partners.service';
import { getTenants } from '../../../../services/tenant.service';

type PartnerFormState = {
  name: string;
  type: PartnerType;
  logoUrl: string;
  partnerUrl: string;
  generalPartner: boolean;
};

const initialPartnerForm: PartnerFormState = {
  name: '',
  type: PartnerType.Lender,
  logoUrl: '',
  partnerUrl: '',
  generalPartner: false
};

export default function PartnersScreen() {
  const history = useHistory();
  const { authUser } = useContext(AuthContext);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [partnerForm, setPartnerForm] = useState<PartnerFormState>(
    initialPartnerForm
  );
  const [isSubmittingPartner, setIsSubmittingPartner] = useState(false);

  const [isUpdateTenantsDialogOpen, setIsUpdateTenantsDialogOpen] = useState(
    false
  );
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [isSubmittingTenant, setIsSubmittingTenant] = useState(false);

  if (!authUser?.isSuperAdmin) {
    history.push('/');
  }

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [partnersResponse, tenantsResponse] = await Promise.all([
        getPartners(),
        getTenants()
      ]);
      setPartners(partnersResponse ?? []);
      setTenants(tenantsResponse ?? []);
    } catch (e) {
      setError(String(e || 'Failed to load data'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const partnerTenantNames = useMemo(() => {
    const tenantNamesByPartnerId = new Map<string, Set<string>>();

    tenants.forEach(tenant => {
      (tenant.partnerIds ?? []).forEach(partnerId => {
        if (!tenantNamesByPartnerId.has(partnerId)) {
          tenantNamesByPartnerId.set(partnerId, new Set<string>());
        }
        tenantNamesByPartnerId
          .get(partnerId)
          ?.add(tenant.name || 'Unnamed Tenant');
      });
    });

    return tenantNamesByPartnerId;
  }, [tenants]);

  const resetPartnerDialogState = () => {
    setIsPartnerDialogOpen(false);
    setEditingPartnerId(null);
    setPartnerForm(initialPartnerForm);
  };

  const openCreatePartnerDialog = () => {
    setEditingPartnerId(null);
    setPartnerForm(initialPartnerForm);
    setIsPartnerDialogOpen(true);
  };

  const openEditPartnerDialog = (partner: Partner) => {
    setEditingPartnerId(partner._id);
    setPartnerForm({
      name: partner.name || '',
      type: partner.type || PartnerType.Lender,
      logoUrl: partner.logoUrl || '',
      partnerUrl: partner.partnerUrl || '',
      generalPartner: Boolean(partner.generalPartner)
    });
    setIsPartnerDialogOpen(true);
  };

  const handlePartnerFormSubmit = async () => {
    setIsSubmittingPartner(true);
    setError('');
    try {
      const payload = {
        name: partnerForm.name,
        type: partnerForm.type,
        logoUrl: partnerForm.logoUrl,
        partnerUrl: partnerForm.partnerUrl,
        generalPartner: partnerForm.generalPartner
      };

      if (editingPartnerId) {
        await updatePartner(editingPartnerId, payload);
      } else {
        await createPartner(payload);
      }

      resetPartnerDialogState();
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to save partner'));
    } finally {
      setIsSubmittingPartner(false);
    }
  };

  const handleDeletePartner = async (partner: Partner) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete partner ${partner.name}?`
    );
    if (!isConfirmed) {
      return;
    }

    setError('');
    try {
      await deletePartner(partner._id);
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to delete partner'));
    }
  };

  const openUpdateTenantsDialog = (partnerId: string) => {
    const partnerTenantIds = tenants
      .filter(tenant => (tenant.partnerIds ?? []).includes(partnerId))
      .map(tenant => tenant._id);

    setSelectedPartnerId(partnerId);
    setSelectedTenantIds(partnerTenantIds);
    setIsUpdateTenantsDialogOpen(true);
  };

  const handleToggleTenantSelection = (tenantId: string, checked: boolean) => {
    setSelectedTenantIds(prev => {
      if (checked) {
        return prev.includes(tenantId) ? prev : [...prev, tenantId];
      }

      return prev.filter(id => id !== tenantId);
    });
  };

  const handleUpdateTenants = async () => {
    if (!selectedPartnerId) {
      return;
    }

    setIsSubmittingTenant(true);
    setError('');
    try {
      await updatePartnerRelations(selectedPartnerId, selectedTenantIds);
      setIsUpdateTenantsDialogOpen(false);
      setSelectedPartnerId('');
      setSelectedTenantIds([]);
      await loadData();
    } catch (e) {
      setError(String(e || 'Failed to update tenants'));
    } finally {
      setIsSubmittingTenant(false);
    }
  };

  return (
    <>
      <Header />
      <Container
        maxWidth="xl"
        component="div"
        disableGutters
        style={{ padding: theme.spacing(4) }}
      >
        <BackToAdminPanel />
        <Grid container direction="column">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3">
              <FormattedMessage {...messages.partners} />
            </Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={openCreatePartnerDialog}
            >
              <FormattedMessage {...messages.createPartner} />
            </Button>
          </Box>

          {error && (
            <Box marginTop={theme.spacing(2)}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box marginTop={theme.spacing(1)}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="25%">
                      <FormattedMessage {...messages.name} />
                    </TableCell>
                    <TableCell width="15%">
                      <FormattedMessage {...messages.generalPartner} />
                    </TableCell>
                    <TableCell width="35%">
                      <FormattedMessage {...messages.tenants} />
                    </TableCell>
                    <TableCell width="25%">
                      <Box display="flex" justifyContent="flex-end">
                        <FormattedMessage {...messages.actions} />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partners.map(partner => (
                    <TableRow key={partner._id}>
                      <TableCell>{partner.name}</TableCell>
                      <TableCell>
                        {partner.generalPartner ? (
                          <FormattedMessage {...messages.yes} />
                        ) : (
                          <FormattedMessage {...messages.no} />
                        )}
                      </TableCell>
                      <TableCell>
                        {Array.from(
                          partnerTenantNames.get(partner._id) || []
                        ).join(', ') || '-'}
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          style={{ gap: theme.spacing(1) }}
                        >
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => openEditPartnerDialog(partner)}
                          >
                            <FormattedMessage {...messages.update} />
                          </Button>
                          <Button
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleDeletePartner(partner)}
                          >
                            <FormattedMessage {...messages.delete} />
                          </Button>
                          <Button
                            size="small"
                            color="default"
                            variant="outlined"
                            onClick={() => openUpdateTenantsDialog(partner._id)}
                          >
                            <FormattedMessage {...messages.updateTenants} />
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!isLoading && partners.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <FormattedMessage {...messages.noPartners} />
                      </TableCell>
                    </TableRow>
                  )}

                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <FormattedMessage {...messages.loading} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Container>

      <Dialog
        open={isPartnerDialogOpen}
        onClose={resetPartnerDialogState}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPartnerId ? (
            <FormattedMessage {...messages.updatePartner} />
          ) : (
            <FormattedMessage {...messages.createPartner} />
          )}
        </DialogTitle>
        <DialogContent>
          <Box
            display="grid"
            style={{ gap: theme.spacing(2), marginTop: theme.spacing(1) }}
          >
            <TextField
              label={messages.name.defaultMessage}
              value={partnerForm.name}
              onChange={event => {
                const { value } = event.target;
                setPartnerForm(prev => ({ ...prev, name: value }));
              }}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>{messages.type.defaultMessage}</InputLabel>
              <Select
                value={partnerForm.type}
                onChange={event => {
                  const value = event.target.value as PartnerType;
                  setPartnerForm(prev => ({
                    ...prev,
                    type: value
                  }));
                }}
              >
                <MenuItem value={PartnerType.Lender}>Lender</MenuItem>
                <MenuItem value={PartnerType.TitleCompany}>
                  Title Company
                </MenuItem>
                <MenuItem value={PartnerType.Valuation}>Products</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={messages.logoUrl.defaultMessage}
              value={partnerForm.logoUrl}
              onChange={event => {
                const { value } = event.target;
                setPartnerForm(prev => ({
                  ...prev,
                  logoUrl: value
                }));
              }}
              fullWidth
            />

            <TextField
              label={messages.partnerUrl.defaultMessage}
              value={partnerForm.partnerUrl}
              onChange={event => {
                const { value } = event.target;
                setPartnerForm(prev => ({
                  ...prev,
                  partnerUrl: value
                }));
              }}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={partnerForm.generalPartner}
                  onChange={event => {
                    const { checked } = event.target;
                    setPartnerForm(prev => ({
                      ...prev,
                      generalPartner: checked
                    }));
                  }}
                  color="primary"
                />
              }
              label={messages.generalPartner.defaultMessage}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetPartnerDialogState}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handlePartnerFormSubmit}
            disabled={isSubmittingPartner || !partnerForm.name.trim()}
          >
            <FormattedMessage {...messages.save} />
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isUpdateTenantsDialogOpen}
        onClose={() => setIsUpdateTenantsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage {...messages.updateTenants} />
        </DialogTitle>
        <DialogContent>
          <Box marginTop="8px">
            <Box
              display="flex"
              flexDirection="column"
              style={{ gap: theme.spacing(1) }}
            >
              {tenants.map(tenant => {
                const tenantName = tenant.name || 'Unnamed Tenant';
                const checked = selectedTenantIds.includes(tenant._id);
                return (
                  <FormControlLabel
                    key={tenant._id}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={event => {
                          const isChecked = event.target.checked;
                          handleToggleTenantSelection(tenant._id, isChecked);
                        }}
                        color="primary"
                      />
                    }
                    label={tenantName}
                  />
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUpdateTenantsDialogOpen(false)}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpdateTenants}
            disabled={isSubmittingTenant}
          >
            <FormattedMessage {...messages.update} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
